import {
  FILTROS_BANDEJA,
  type EnviarMensajeInput,
  type FiltroBandeja,
  type LeadCalificarInput,
} from '@app/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { qk } from '../../../lib/queryKeys';
import { campanasApi } from '../../admin/api/campanas.api';
import { useCurrentUser } from '../../auth/hooks/useCurrentUser';
import { useInstancias } from '../api/instancias.api';
import { leadsApi } from '../api/leads.api';
import { sugerenciasApi } from '../api/sugerencias.api';
import { Conversacion } from '../components/Conversacion';
import { ListaLeads, ORDENES_BANDEJA, type OrdenBandeja } from '../components/ListaLeads';
import { useEventosLeads } from '../hooks/useEventosLeads';

// La bandeja: lista a la izquierda, conversación a la derecha. El lead elegido
// vive en la URL (/bandeja/:leadId) para poder compartir/recargar; el filtro y la
// búsqueda en el query string.
export function BandejaPage(): JSX.Element {
  const es = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { leadId } = useParams<{ leadId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: user } = useCurrentUser();
  const { conectado } = useEventosLeads();
  const { instancias, porNombre } = useInstancias();

  const filtroParam = searchParams.get('filtro');
  const filtro: FiltroBandeja = FILTROS_BANDEJA.includes(filtroParam as FiltroBandeja)
    ? (filtroParam as FiltroBandeja)
    : 'todos';
  // Ciclo 1c: filtrar por campaña (id en la URL, como el filtro).
  const campana = searchParams.get('campana') ?? '';
  // Filtro por etapa (30/09/2026, pedido de Javier): independiente de `filtro`
  // (que es por estado) — ver todos los leads de la etapa 3, sin importar en
  // qué pestaña de estado están.
  const etapaParam = Number(searchParams.get('etapa'));
  const etapa = etapaParam >= 1 && etapaParam <= 5 ? etapaParam : undefined;
  // 01/10/2026: orden de la lista (id en la URL, mismo patrón que filtro/campaña/etapa).
  const ordenParam = searchParams.get('orden');
  const orden: OrdenBandeja = ORDENES_BANDEJA.includes(ordenParam as OrdenBandeja)
    ? (ordenParam as OrdenBandeja)
    : 'fecha_desc';
  const campanasQuery = useQuery({ queryKey: qk.campanas.all, queryFn: campanasApi.list, staleTime: 60_000 });
  const [buscar, setBuscar] = useState('');
  const [buscarDebounced, setBuscarDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBuscarDebounced(buscar.trim()), 250);
    return () => clearTimeout(t);
  }, [buscar]);

  const listaQuery = useQuery({
    queryKey: qk.leads.list({ filtro, buscar: buscarDebounced, campana, ...(etapa ? { etapa } : {}) }),
    queryFn: () =>
      leadsApi.list({
        filtro,
        ...(buscarDebounced ? { buscar: buscarDebounced } : {}),
        ...(campana ? { campana } : {}),
        ...(etapa ? { etapa } : {}),
      }),
    refetchInterval: conectado ? false : 15_000, // sin SSE, sondeo suave
  });

  const mensajesQuery = useQuery({
    queryKey: qk.leads.mensajes(leadId ?? ''),
    queryFn: () => leadsApi.mensajes(leadId ?? ''),
    enabled: !!leadId,
  });

  // El lead elegido sale de la lista mientras carga el detalle (evita el parpadeo en
  // blanco), pero el detalle SIEMPRE se pide: es el único que trae `sugerenciaPendiente`
  // real (la lista la hardcodea en null para no pagar una consulta extra por fila) —
  // pedirlo solo "si no está en la lista" dejaba el borrador del Ciclo 2 sin mostrarse
  // nunca, porque el lead abierto casi siempre está en la lista (bug real, 21/09).
  const leadEnLista = useMemo(
    () => listaQuery.data?.leads.find((l) => l.id === leadId),
    [listaQuery.data, leadId],
  );
  const leadQuery = useQuery({
    queryKey: qk.leads.detail(leadId ?? ''),
    queryFn: () => leadsApi.get(leadId ?? ''),
    enabled: !!leadId,
  });
  const lead = leadQuery.data?.lead ?? leadEnLista;

  // Al abrir una conversación con no leídos, se marca leída.
  useEffect(() => {
    if (lead && lead.noLeidos > 0) {
      void leadsApi
        .marcarLeido(lead.id)
        .then(() => queryClient.invalidateQueries({ queryKey: qk.leads.all }));
    }
  }, [lead, queryClient]);

  const invalidarLead = (id: string): void => {
    void queryClient.invalidateQueries({ queryKey: qk.leads.all });
    void queryClient.invalidateQueries({ queryKey: qk.leads.mensajes(id) });
  };

  const enviarMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: EnviarMensajeInput }) => leadsApi.enviar(id, input),
    onSuccess: (_d, v) => {
      invalidarLead(v.id);
    },
    onError: (e) => {
      toast.error(getErrorMessage(e, es.bandeja.compositor.errorEnvio));
    },
  });

  const enviarAudioMutation = useMutation({
    mutationFn: ({
      id,
      blob,
      meta,
    }: {
      id: string;
      blob: Blob;
      meta: { instancia?: string; fijarComoPreferida?: boolean };
    }) => leadsApi.enviarAudio(id, blob, meta),
    onSuccess: (_d, v) => {
      invalidarLead(v.id);
    },
    onError: (e) => {
      toast.error(getErrorMessage(e, es.bandeja.audio.errorEnvio));
    },
  });

  const bloquearMutation = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) => leadsApi.bloquear(id, { motivo }),
    onSuccess: (_d, v) => {
      toast.success(es.bandeja.acciones.eliminado);
      invalidarLead(v.id);
      // El lead ya no está en ninguna vista de trabajo (bloqueados tiene su propia pestaña).
      navigate('/bandeja');
    },
    onError: (e) => {
      toast.error(getErrorMessage(e, es.bandeja.acciones.errorEliminar));
    },
  });

  const cerrarMutation = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: 'GANADO' | 'NO_INTERESADO' }) =>
      leadsApi.cerrar(id, { motivo }),
    onSuccess: (_d, v) => {
      toast.success(es.bandeja.acciones.cerrado);
      invalidarLead(v.id);
    },
    onError: (e) => {
      toast.error(getErrorMessage(e, es.bandeja.acciones.errorCerrar));
    },
  });

  const etapaMutation = useMutation({
    mutationFn: ({ id, etapa }: { id: string; etapa: number }) => leadsApi.actualizar(id, { etapa }),
    onSuccess: (_d, v) => {
      invalidarLead(v.id);
    },
    onError: (e) => {
      toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal));
    },
  });

  // ciclo-calidad-leads (R6): la calidad del lead, desde el ícono de la charla.
  const calidadMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: LeadCalificarInput }) => leadsApi.calificar(id, input),
    onSuccess: (_d, v) => {
      invalidarLead(v.id);
    },
    onError: (e) => {
      toast.error(getErrorMessage(e, es.bandeja.calidad.error));
    },
  });

  // Slice B (D-027): calificación de 1 a 5 estrellas de la sugerencia pendiente.
  const calificarMutation = useMutation({
    mutationFn: (v: { leadId: string; sugerenciaId: string; calificacion: number }) =>
      sugerenciasApi.calificar(v.sugerenciaId, v.calificacion),
    onSuccess: (_d, v) => {
      invalidarLead(v.leadId);
    },
    onError: (e) => {
      toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal));
    },
  });

  // Botón "generar ahora" (29/09/2026): salta la ventana de agrupación. La
  // sugerencia en sí llega por SSE (invalidarLead ya la va a traer), acá solo
  // se dispara — por eso no hay onSuccess con datos.
  const generarAhoraMutation = useMutation({
    mutationFn: (leadId: string) => sugerenciasApi.generarAhora(leadId),
    onError: (e) => {
      toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal));
    },
  });

  // Respaldo de las últimas páginas de CADA número (si n8n estuvo caído un rato).
  const sincronizarMutation = useMutation({
    mutationFn: async () => {
      const total = { procesados: 0, duplicados: 0, ignorados: 0 };
      for (const i of instancias) {
        const r = await leadsApi.sincronizar({ modo: 'respaldo', instancia: i.nombre, paginas: 2 });
        total.procesados += r.procesados;
        total.duplicados += r.duplicados;
        total.ignorados += r.ignorados;
      }
      return total;
    },
    onSuccess: (r) => {
      toast.success(
        es.bandeja.acciones.sincronizado
          .replace('{procesados}', String(r.procesados))
          .replace('{duplicados}', String(r.duplicados))
          .replace('{ignorados}', String(r.ignorados)),
      );
      void queryClient.invalidateQueries({ queryKey: qk.leads.all });
    },
    onError: (e) => {
      toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal));
    },
  });

  const onEnviar = async (input: EnviarMensajeInput): Promise<void> => {
    if (!lead) return;
    await enviarMutation.mutateAsync({ id: lead.id, input });
  };

  const onEnviarAudio = async (
    blob: Blob,
    meta: { instancia?: string; fijarComoPreferida?: boolean },
  ): Promise<void> => {
    if (!lead) return;
    await enviarAudioMutation.mutateAsync({ id: lead.id, blob, meta });
  };

  const onEliminar = async (motivo: string): Promise<void> => {
    if (!lead) return;
    await bloquearMutation.mutateAsync({ id: lead.id, motivo });
  };

  // Filtro, campaña, etapa y orden conviven en la URL: cambiar uno no puede pisar
  // los otros tres (antes cada onXxx armaba el searchParams por separado y "orden"
  // se hubiera perdido al tocar cualquiera de los otros).
  const actualizarQuery = (cambios: {
    filtro?: FiltroBandeja;
    campana?: string;
    etapa?: number | undefined;
    orden?: OrdenBandeja;
  }): void => {
    const next = {
      filtro: 'filtro' in cambios ? cambios.filtro : filtro,
      campana: 'campana' in cambios ? cambios.campana : campana,
      etapa: 'etapa' in cambios ? cambios.etapa : etapa,
      orden: cambios.orden ?? orden,
    };
    setSearchParams({
      ...(next.filtro === 'todos' ? {} : { filtro: next.filtro }),
      ...(next.campana ? { campana: next.campana } : {}),
      ...(next.etapa ? { etapa: String(next.etapa) } : {}),
      ...(next.orden === 'fecha_desc' ? {} : { orden: next.orden }),
    });
  };

  return (
    <div className="flex h-[calc(100vh-6.5rem)] min-h-[480px] flex-col">
      <div className="flex items-center justify-between border-b border-border-subtle bg-surface px-4 py-2">
        <h1 className="font-titulos text-base font-bold text-text-strong">{es.bandeja.titulo}</h1>
        <div className="flex items-center gap-3 text-xs text-text-subtle">
          <span
            className={cn('inline-flex items-center gap-1', conectado ? 'text-success' : 'text-text-faint')}
            title={conectado ? es.bandeja.conexion.enVivo : es.bandeja.conexion.reconectando}
          >
            {conectado ? <Wifi size={13} /> : <WifiOff size={13} />}
            {conectado ? es.bandeja.conexion.enVivo : es.bandeja.conexion.reconectando}
          </span>
          <Button variant="outline" size="sm" asChild>
            <Link to="/bandeja/indicadores">
              <BarChart3 size={13} />
              {es.bandeja.acciones.indicadores}
            </Link>
          </Button>
          {user?.rol === 'ADMINISTRADOR' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => sincronizarMutation.mutate()}
              disabled={sincronizarMutation.isPending || instancias.length === 0}
            >
              <RefreshCw size={13} className={cn(sincronizarMutation.isPending && 'animate-spin')} />
              {sincronizarMutation.isPending
                ? es.bandeja.acciones.sincronizando
                : es.bandeja.acciones.sincronizar}
            </Button>
          )}
        </div>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[360px_1fr]">
        <aside
          className={cn('min-h-0 border-r border-border-subtle bg-surface', leadId && 'hidden md:block')}
        >
          <ListaLeads
            leads={listaQuery.data?.leads ?? []}
            conteos={listaQuery.data?.conteos ?? {}}
            filtro={filtro}
            onFiltro={(f) => actualizarQuery({ filtro: f })}
            campanas={campanasQuery.data?.campanas ?? []}
            campana={campana}
            onCampana={(c) => actualizarQuery({ campana: c })}
            etapa={etapa}
            onEtapa={(e) => actualizarQuery({ etapa: e })}
            orden={orden}
            onOrden={(o) => actualizarQuery({ orden: o })}
            buscar={buscar}
            onBuscar={setBuscar}
            seleccionadoId={leadId ?? null}
            onSeleccionar={(id) => navigate({ pathname: `/bandeja/${id}`, search: searchParams.toString() })}
            cargando={listaQuery.isPending}
            error={listaQuery.isError}
          />
        </aside>
        <section className={cn('min-h-0', !leadId && 'hidden md:block')}>
          {lead ? (
            <Conversacion
              lead={lead}
              mensajes={mensajesQuery.data?.mensajes ?? []}
              instancias={instancias}
              porNombre={porNombre}
              cargando={mensajesQuery.isPending}
              enviando={enviarMutation.isPending}
              enviandoAudio={enviarAudioMutation.isPending}
              eliminando={bloquearMutation.isPending}
              onEnviar={onEnviar}
              onEnviarAudio={onEnviarAudio}
              onEliminar={onEliminar}
              onCerrar={(motivo) => cerrarMutation.mutate({ id: lead.id, motivo })}
              onCambiarEtapa={(etapa) => etapaMutation.mutate({ id: lead.id, etapa })}
              onCalificarSugerencia={(sugerenciaId, calificacion) =>
                calificarMutation.mutate({ leadId: lead.id, sugerenciaId, calificacion })
              }
              onCalificarLead={(input) => calidadMutation.mutate({ id: lead.id, input })}
              calificandoLead={calidadMutation.isPending}
              onGenerarAhora={() => generarAhoraMutation.mutate(lead.id)}
              generandoAhora={generarAhoraMutation.isPending}
            />
          ) : (
            <div className="flex h-full items-center justify-center bg-surface-sunken p-8 text-center text-sm text-text-faint">
              {leadId && leadQuery.isPending ? es.bandeja.cargando : es.bandeja.elegiUnLead}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
